import { useDispatch } from "react-redux";
import { 
  savePlanRequest, 
  updatePlanRequest, 
  saveDayPlanRequest, 
  saveDayPlanDetailRequest,
  updateDayPlanRequest,
  deletePlanRequest ,
  deleteDayPlanDetailRequest,
  readTWGPlansRequest
} from "../../../store/Plan/planActions";
import { useState } from "react";
import { fetchChurchesRequest } from "../../../store/Churches/actions";

export const GlobalPlansHooks = () => {
  const dispatch = useDispatch();
  const [reloadData, setReloadData] = useState(false);
  const [value, setValue] = useState("");

  const onChangeSearchBar = (e) => {
    setValue(e.target.value);
  };

  const fetchAllPlans = (page, limit, searchCriteria = {}, callbackFn, churchId = "", typeonplan, typeofentity) => {
    dispatch(
      readTWGPlansRequest({
        page: page,
        limit: limit,
        typeonplan: typeonplan || 1,
        ...searchCriteria,
        searchText: searchCriteria.searchText || value
      }, (res) => {
        if (res.type === 1) {
          const mappedData = (res.data || []).map(plan => {
            // Handle potentially empty object for dates
            const rawDate = plan.lastUpdatedDate || plan.createdDate;
            let formattedDate = "";

            if (rawDate && typeof rawDate === 'object' && Object.keys(rawDate).length === 0) {
              formattedDate = ""; // It's an empty object {}
            } else if (rawDate) {
              formattedDate = rawDate;
            }

            return {
              ...plan,
              planObjId: plan._id,
              plan_title: plan.planName || "Untitled Plan",
              plan_description: plan.description || "",
              noOfDays: plan.noofDays || "0",
              createdDate: formattedDate,
              lastUpdatedDate: formattedDate,
              dayPlans: plan.dayplans || []
            };
          });

          if (callbackFn) {
            callbackFn({
              data: mappedData,
              total: res.total || mappedData.length,
              page: page,
              limit: limit
            });
          }
        } else if (callbackFn) {
          callbackFn({
            data: [],
            total: 0,
            page: page,
            limit: limit
          });
        }
      })
    );
  };

  const handleDelete = (row, callback) => {
    dispatch(deletePlanRequest({ _id: row.planObjId || row._id }, (res) => {
      if (res.type === 1) {
        setReloadData(prev => !prev);
      }
      if (callback) callback(res);
    }));
  };

  return { 
    reloadData, 
    setReloadData, 
    fetchAllPlans, 
    dispatch, 
    value, 
    onChangeSearchBar, 
    handleDelete 
  };
};

export const handleSavePlanGlobal = async (dispatch, planData, galleryObjId, dayPlans, callback, typeofentity) => {
  const payload = {
    ...planData,
    plansCoverImageUrl: galleryObjId,
    typeonplan: 1
  };

  dispatch(savePlanRequest(payload, async (res) => {
    if (Number(res?.type) !== 1) {
      callback?.({ type: 0, message: res?.message || "Failed to save main plan" });
      return;
    }

    let planObjId = res?._id || res?.planObjId;
    if (res?.data) {
      if (Array.isArray(res.data) && res.data.length > 0) {
        planObjId = res.data[0]._id || res.data[0].planObjId;
      } else if (typeof res.data === 'object') {
        planObjId = res.data._id || res.data.planObjId;
      }
    }

    if (!planObjId) {
      callback?.({ type: 0, message: "No plan ID received from server" });
      return;
    }

    if (!dayPlans || !Array.isArray(dayPlans) || dayPlans.length === 0) {
      callback?.({ type: 1, message: "Plan saved successfully!", data: { planObjId } });
      return;
    }

    // Save day plans
    for (let dayIndex = 0; dayIndex < dayPlans.length; dayIndex++) {
      const day = dayPlans[dayIndex];
      const dayPlanBasicPayload = {
        _id: planObjId,
        dayNumber: day.dayNumber || (dayIndex + 1),
        dayplanname: day.dayPlanName || `Day ${dayIndex + 1}`,
        dayplandescription: day.dayPlanDescription || "",
      };

      await new Promise((resolveDay) => {
        dispatch(saveDayPlanRequest(dayPlanBasicPayload, async (saveRes) => {
          if (Number(saveRes?.type) !== 1) {
            resolveDay();
            return;
          }

          const dayplanObjId = saveRes.dayplanObjId || saveRes._id;
          if (!dayplanObjId) {
            resolveDay();
            return;
          }

          const verses = day.verseObjId || [];
          if (verses.length === 0 && day.youtubeVideoUrl) {
            await new Promise((resolveDetail) => {
              dispatch(saveDayPlanDetailRequest({
                _id: planObjId,
                dayplanObjId,
                youtubevideourl: day.youtubeVideoUrl,
                versionid: "", bookId: "", chapterId: "", verseStart: "", bookName: "", verseText: ""
              }, resolveDetail));
            });
          } else {
            for (let v = 0; v < verses.length; v++) {
              const verse = verses[v];
              await new Promise((resolveDetail) => {
                dispatch(saveDayPlanDetailRequest({
                  _id: planObjId,
                  dayplanObjId,
                  bookId: verse.book_id || "",
                  bookName: verse.book_name || "",
                  chapterId: String(verse.chapter || ""),
                  verseStart: String(verse.verse_start || ""),
                  verseText: verse.text || "",
                  versionid: verse.filesetid || "",
                  youtubevideourl: (v === 0) ? (day.youtubeVideoUrl || "") : "",
                }, resolveDetail));
              });
            }
          }
          resolveDay();
        }));
      });
    }

    callback?.({ type: 1, message: "Plan saved successfully!", data: { planObjId } });
  }));
};

export const handleUpdatePlanGlobal = async (dispatch, rowData, planData, galleryObjId, dayPlans, callback, typeofentity) => {
  const planObjId = rowData?.planObjId || rowData?._id;
  if (!planObjId) {
    callback?.({ type: 0, message: "Plan ID missing" });
    return;
  }

  const payload = {
    _id: planObjId,
    planName: planData.planName,
    description: planData.description,
    noofDays: planData.noofDays,
    plansCoverImageUrl: galleryObjId,
    categoryObjId: planData.categoryObjId,
    layoutStyle: planData.layoutStyle
  };

  dispatch(updatePlanRequest(payload, async (res) => {
    if (Number(res.type) !== 1) {
      callback?.({ type: 0, message: res.message });
      return;
    }

    for (let i = 0; i < dayPlans.length; i++) {
      const incomingDay = dayPlans[i];
      const existingDay = rowData?.dayPlans?.find(d => d.dayNumber === incomingDay.dayNumber) || rowData?.dayPlans?.[i];
      let dayplanObjId = existingDay?._id || existingDay?.dayplanObjId;

      if (dayplanObjId) {
        await new Promise(resolve => {
          dispatch(updateDayPlanRequest({
            _id: planObjId,
            dayplanObjId,
            dayNumber: incomingDay.dayNumber || i + 1,
            dayplanname: incomingDay.dayPlanName,
            dayplandescription: incomingDay.dayPlanDescription
          }, resolve));
        });
      } else {
        const saveRes = await new Promise(resolve => {
          dispatch(saveDayPlanRequest({
            _id: planObjId,
            dayNumber: incomingDay.dayNumber || i + 1,
            dayplanname: incomingDay.dayPlanName,
            dayplandescription: incomingDay.dayPlanDescription
          }, resolve));
        });
        if (Number(saveRes.type) === 1) {
          dayplanObjId = saveRes.dayplanObjId || saveRes._id;
        }
      }
    }

    callback?.({ type: 1, message: "Plan updated successfully" });
  }));
};
